import { ApolloError } from '@apollo/client';
import { captureException } from '@/utils/sentry';
import { getDeviceTimezone } from '@/utils/date';
import { partnerSupplier } from '@/utils/environment';
import { ProspectDetailsUpdateInput } from '@/types';
import { SalesPortalRepViewProspectsDocument } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/documents/sales-portal-rep-view-prospects.generated';
import { useSalesPortalUpdateProspectDetailsMutation } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/documents/sales-portal-update-prospect-details.generated';

export interface UpdateProspectDetailsInput {
  prospectDetails: ProspectDetailsUpdateInput;
  restaurantUUID: string;
  supplierUUID: string;
}

export interface UpdateProspectDetailsMutatorOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

export interface UpdateProspectDetailsMutatorResult {
  error: ApolloError | undefined;
  loading: boolean;
  updateProspectDetails: (input: UpdateProspectDetailsInput) => Promise<void>;
}

/**
 * Mutator to update prospect details.
 *
 * @param options.onError - Callback invoked when the mutation fails
 * @param options.onSuccess - Callback invoked when the mutation succeeds
 * @returns {@link UpdateProspectDetailsMutatorResult}
 */
export const useUpdateProspectDetailsMutator = (
  options?: UpdateProspectDetailsMutatorOptions,
): UpdateProspectDetailsMutatorResult => {
  const [updateProspectDetailsMutation, { loading, error }] =
    useSalesPortalUpdateProspectDetailsMutation();

  const updateProspectDetails = async (
    input: UpdateProspectDetailsInput,
  ): Promise<void> => {
    const { prospectDetails, restaurantUUID, supplierUUID } = input;

    try {
      await updateProspectDetailsMutation({
        refetchQueries: [
          {
            query: SalesPortalRepViewProspectsDocument,
            variables: {
              businessOrganizationUUID:
                partnerSupplier.businessOrganizationUUID,
              userTimezone: getDeviceTimezone(),
            },
          },
        ],
        variables: {
          prospectDetails,
          restaurantUUID,
          supplierUUID,
        },
      });

      options?.onSuccess?.();
    } catch (err) {
      const mutationError = err instanceof Error ? err : new Error(String(err));
      captureException(mutationError);
      options?.onError?.(mutationError);
    }
  };

  return {
    error,
    loading,
    updateProspectDetails,
  };
};
